import promise from 'promise';

// client cookies
import Cookies from 'js-cookie';
import { COOKIE } from 'app/client/workers/constants';

import ErrorRMW from 'app/shared/model/ErrorRMW';

// service
import appLogger from 'app/shared/loggers/appLogger';

import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import NotificationActions from 'app/shared/actions/NotificationActions';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import StringConstants from 'app/shared/constants/StringConstants';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import contextUtils from 'app/shared/utils/contextUtils';

// constants
const KEY = {
    publicKey: 'BGrtzhPAZZDp1rJzGDZH0nBM1CO1TiDB8n5YKS7RbtMqeW0PaILNJLlWXi6macaUVRaGdxm-FVbvyVMNhvXY59M',
};

const NOTIFICATION_PERMISSION = {
    DEFAULT: 'default',
    DENIED: 'denied',
    GRANTED: 'granted',
};

const NOTIFICATION_TYPE = {
    LEAD_ADDED: 'leadAdded',
    LISTING_EXPIRED: 'listingExpired',
    LISTING_NEARLY_EXPIRED: 'listingNearlyExpired',
    SYNDICATION_STATUS_UPDATED: 'syndicationStatusUpdated',
    BILLING_PORTAL_PAYMENT_METHODS_REFRESH: 'billingPortalPaymentMethodsRefresh',
    BILLING_PORTAL_PAYMENT_COMPLETE: 'billingPortalPaymentComplete',
};

const NOTIFICATION_OPTION = {
    // 5 seconds
    PRE_PROMPT_DELAY: 5000,
};

// debug setting
const DISABLE_NOTIFICATION_PERSIST = false;

// module properties
const prePrompText = 'Would you like to receive notifications when you get new leads ' +
    'or updates to your listing status?';

let brand = null;
let dispatch = null;
let registration = null;
let subscriptionObj = {};

//-------------------------
// getters & setters
//-------------------------
const setBrand = (value) => {
    brand = value;
};

const getBrand = () => brand;

const setDispatch = (value) => {
    dispatch = value;
};

const getDispatch = () => dispatch;

const setRegistration = (value) => {
    registration = value;
};

const getRegistration = () => registration;

const setSubscriptionObj = (value) => {
    console.info('setSubscriptionObj', value, JSON.stringify(value));
    subscriptionObj = value;
};

const getSubscriptionObj = () => subscriptionObj;

//-------------------------
// methods
//-------------------------
/*
 * getPrompText
 *
 * Returns the verbiage when the user is asked if they would like to receive notifications
 */
const getPrePrompText = () => prePrompText;

/*
 * getSubscription
 *
 * @registration
 *
 * Requests the subscription from the pushManager
 */
const getSubscription = registration => registration.pushManager.getSubscription()
    .then((subscription) => {
        if (!subscription) {
            // not subscribed
            return false;
        }

        setSubscriptionObj(subscription);

        return subscription;
    })
    .catch((error) => {
        // message: "Registration failed - missing applicationServerKey, and gcm_sender_id not found in manifest"
        console.error('ERROR @getSubscription', error);
    });

const hasNotificationFeature = () => ('serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
);

/*
 * urlBase64ToUint8Array
 *
 * When using your VAPID key in your web app, you'll need to convert the URL safe base64 string
 * to a Uint8Array to pass into the subscribe call, which you can do like so:
 *
 * ref: https://www.npmjs.com/package/web-push#using-vapid-key-for-applicationserverkey
 */

/* eslint-disable no-mixed-operators, no-useless-escape */
const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};
/* eslint-enable no-mixed-operators, no-useless-escape */

const isSubscribed = () => Boolean(getSubscriptionObj() && getSubscriptionObj().endpoint);

/*
 * subscribe
 *
 * @registration
 *
 * Create new notification subscription and persiste to database.
 */
const subscribe = (registration = getRegistration()) => {
    // registration is required
    if (!registration) {
        return promise.reject(ErrorRMW({
            data: registration,
            message: 'ERROR @subscribe registration is falsy',
        }));
    }

    // create encrypted vapid key
    const vapidPublicKey = KEY.publicKey;
    const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

    const options = {
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey,
    };

    return registration.pushManager.subscribe(options)
        .then(
            (subscription) => {
                setSubscriptionObj(subscription);

                // persist in db
                if (!DISABLE_NOTIFICATION_PERSIST) {
                    const dispatch = getDispatch();
                    dispatch(NotificationActions.register(subscription));
                }

                return subscription;
            }, (error) => {
                // TODO: add splunk formatting
                console.error(error);
            }
        );
};

const unsubscribe = () => {
    if (isSubscribed()) {
        return getSubscriptionObj().unsubscribe()
            .then(() => {
                const subscription = getSubscriptionObj();
                setSubscriptionObj(null);

                // persist in db
                if (!DISABLE_NOTIFICATION_PERSIST) {
                    const dispatch = getDispatch();
                    dispatch(NotificationActions.unregister(subscription));
                }

                // return previous subscription
                return subscription;
            });
    }

    return false;
};

const requestPermission = () => {
    const dispatch = getDispatch();

    // get friendly ga label with context
    const labelPrefix = 'Browser-Request - ';
    const label = contextUtils.getPageContext(labelPrefix);

    dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.NOTIFICATION_PROMPT_DISPLAYED, { label }));

    Notification.requestPermission()
        .then((result) => {
            switch (result) {
                case NOTIFICATION_PERMISSION.DEFAULT:
                    // [blocked] - the permission request was dismissed'
                    dispatch(analyticsUtils.track.GAEventV2(
                        AnalyticsConstants.GA_EVENTS.NOTIFICATION_DISMISSED, { label }
                    ));
                    break;

                case NOTIFICATION_PERMISSION.DENIED:
                    // [blocked] - The permission request was denied.
                    dispatch(analyticsUtils.track.GAEventV2(
                        AnalyticsConstants.GA_EVENTS.NOTIFICATION_PROMPT_DENIED, { label }
                    ));
                    break;

                case NOTIFICATION_PERMISSION.GRANTED:
                    // [allow] - the permission request was accepted.
                    dispatch(analyticsUtils.track.GAEventV2(
                        AnalyticsConstants.GA_EVENTS.NOTIFICATION_PROMPT_GRANTED, { label }
                    ));

                    // if there is no subscription
                    if (!isSubscribed()) {
                        subscribe();
                    }
                    break;

                default:
                    console.error('ERROR: no case for ', result);
                    break;
            }
        })
        .catch((error) => {
            const errorRMWModel = ErrorRMW({
                data: error,
                message: 'ERROR @notificationModule.requestPermission',
            });

            // dispatch
            appLogger.logError({
                message: errorRMWModel.message,
                context: 'notificationModule.requestPermission',
            });
        });
};

/*
 * canRequestPermission
 *
 * Checks notification permission and cookies value
 * if returns true then the browser will request notification permissions
 */
const canRequestPermission = () => {
    const brand = getBrand();
    const hasFeature = hasNotificationFeature();

    return (hasFeature &&
        brand === StringConstants.UNFORMATTED_BRAND_NAMES.zillow &&
        Notification.permission === NOTIFICATION_PERMISSION.DEFAULT &&
        Cookies.get(COOKIE.ZG_MODAL_NOTIFICATION_PROMPT_DISPLAY_DECLINED) !== 'true');
};

const setNotificationPromptCookieAsDeclined = () => {
    // to expire in minutes use: minutes / (60 * 24)
    const expireDays = 30;
    Cookies.set(COOKIE.ZG_MODAL_NOTIFICATION_PROMPT_DISPLAY_DECLINED, true, {
        expires: expireDays,
    });
};

/*
 * doPermissionChecks
 *
 * @displayModal - display pre notification prompt if true
 *
 * Checks if we can request notification permission or auto subscribe to notification service
 */
const doPermissionChecks = ({ displayModal = true }) => {
    if (!navigator.serviceWorker) {
        return false;
    }

    navigator.serviceWorker.ready.then(() => {
        // delay permissions check so brand can be set
        const delay = 10;

        setTimeout(() => {
            // check if default permissions and if preliminary prompt was not declined
            if (canRequestPermission()) {
                // display pre notification prompt
                if (displayModal) {
                    const dispatch = getDispatch();
                    dispatch(ModalActions.showModal({
                        modalType: ModalConstants.NOTIFICATION_MODAL,
                        className: 'NotificationPromptModal',
                        header: 'Receive Notifications',
                        onOpen: () => dispatch(analyticsUtils.track.GAEventV2(
                            AnalyticsConstants.GA_EVENTS.NOTIFICATION_PRE_PROMPT_DISPLAYED, {
                                label: contextUtils.getPageContext('Pre-Prompt - '),
                            },
                        )),
                        onClose: () => {
                            dispatch(analyticsUtils.track.GAEventV2(
                                AnalyticsConstants.GA_EVENTS.NOTIFICATION_PRE_PROMPT_DISMISSED, {
                                    label: contextUtils.getPageContext('Pre-Prompt - '),
                                },
                            ));
                            setNotificationPromptCookieAsDeclined();
                        },
                        useModalDialog: false,
                    }));
                } else {
                    requestPermission();
                }
            } else if (Notification.permission === NOTIFICATION_PERMISSION.GRANTED) {
                // permission already granted
                // check if already subscribed
                if (!isSubscribed()) {
                    // auto subscribe
                    subscribe();
                }
            }
        }, delay);
    });

    return false;
};

const showNotification = (body = '', notificationResponseModel = {}) => {
    if (Notification.permission === NOTIFICATION_PERMISSION.GRANTED) {
        const title = 'Zillow Notification';

        const options = {
            body,
            icon: '/rental-manager/public/bn-icon-144x144.png',
            // actions: [{
            //     action: 'view',
            //     title: 'View New Lead',
            //     icon: '/rental-manager/public/icon-72x72.png',
            //     maxActions: 2
            // }],
            requireInteraction: false,
            silent: false,
            sound: '/path/to/audio.ext',
            vibrate: [200, 100, 200],
            dir: 'ltr',
            lang: 'en-US',
            timestamp: Date.now(),
            data: notificationResponseModel,
        };

        if (self.registration) {
            return self.registration.showNotification(title, options);
        }
    }

    return promise.resolve();
};

const handleSWReady = (registration, dispatch, brand) => {
    setRegistration(registration);
    setDispatch(dispatch);
    setBrand(brand);

    return getSubscription(registration);
};

export default {
    canRequestPermission,
    doPermissionChecks,
    getPrePrompText,
    getSubscriptionObj,
    getRegistration,
    handleSWReady,
    hasNotificationFeature,
    isSubscribed,
    requestPermission,
    setNotificationPromptCookieAsDeclined,
    showNotification,
    subscribe,
    unsubscribe,
};

export {
    NOTIFICATION_PERMISSION,
    NOTIFICATION_TYPE,
    NOTIFICATION_OPTION,
};
